import React, { useEffect, useState, useCallback } from 'react'
import { BORDER_RADIUS } from '../../../theme/muiTheme';
import { toast } from 'react-toastify';
import validator from 'validator'
import format from 'date-fns/format'

import { Auth, API, Storage } from 'aws-amplify'
import AWS from 'aws-sdk'
import { updateAuthAccount, updateOrganization } from '../../../managers/graphql/mutations'

import { useAppDispatch, useAppSelector } from '../../../hooks';
import { cognitoConfirmSignUp, reloadAuthAccount } from '../../../managers/redux/slices/authAccount';
import { getOrganizationHeaderImage, updateHeaderImage } from '../../../managers/redux/slices/organization';

import LocationSelector from './../../../components/inputs/LocationSelector'

import { useDropzone } from 'react-dropzone'

// mui
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl'
import CircularProgress from '@mui/material/CircularProgress';
import PhoneNumberInput from 'material-ui-phone-number';

import ProfileAvatar from '../../../components/data-display/ProfileAvatar'

import { StaffProfile, AuthAccount, Organization, Location } from '../../../API'

interface ManageOrganizationProps {
    visible: boolean
    onClose: () => void
}

export default function ManageOrganization(props: ManageOrganizationProps) {

    const dispatch = useAppDispatch()

    useEffect(() => {
        setVisible(props.visible)
    }, [props.visible])

    function handleClose() {
        setVisible(false)
        props.onClose()
    }

    const [visible, setVisible] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)

    const [name, setName] = useState<string | null>(null)
    const [shortName, setShortName] = useState<string | null>(null)
    const [location, setLocation] = useState<Location | null>(null)
    const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
    const [website, setWebsite] = useState<string | null>(null)
    const [description, setDescription] = useState<string | null>(null)

    const [changeHeaderImage, setChangeHeaderImage] = useState<boolean>(false)
    const [uploadedHeaderImageFile, setUploadedHeaderImageFile] = useState<any>(null)

    const [changeProfilePicture, setChangeProfilePicture] = useState<boolean>(false)
    const [uploadedProfilePictureFile, setUploadedProfilePictureFile] = useState<any>(null)

    const authAccount: AuthAccount | null | undefined = useAppSelector(state => state.authAccount.data)
    const selectedStaffProfile: StaffProfile | null = useAppSelector(state => state.authAccount.selectedStaffProfile)
    const organizationHeaderImage: string | null = useAppSelector(state => state.organization.organizationHeaderImage)

    async function _handleSaveChanges() {
        if (selectedStaffProfile && selectedStaffProfile.organizationID) {

            var validatedPhoneNumber: string | null = null
            var validatedWebsite: string | null = null

            if (phoneNumber && validator.isMobilePhone(phoneNumber)) validatedPhoneNumber = phoneNumber
            if (website && validator.isURL(website)) validatedWebsite = website

            const updateOrganizationResponse: any = await API.graphql({
                query: updateOrganization,
                variables: {
                    input: {
                        id: selectedStaffProfile.organizationID,
                        shortName: shortName,
                        phoneNumber: validatedPhoneNumber,
                        website: validatedWebsite,
                        locationID: location ? location.id : null,
                        description: description
                    }
                }
            })

            console.log('updated organization: ', updateOrganizationResponse.data)

            if (updateOrganizationResponse.data && updateOrganizationResponse.data.updateOrganization) {
                toast.success('Saved organization information', {
                    position: "bottom-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });
                setShortName(null)
                setName(null)
                setPhoneNumber(null)
                setWebsite(null)
                props.onClose()
                window.location.reload()
            }
        }
    }

    useEffect(() => {
        if (selectedStaffProfile && selectedStaffProfile.organization) {
            setName(selectedStaffProfile.organization.name)
        }
        if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.shortName) {
            setShortName(selectedStaffProfile.organization.shortName)
        }
        if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.phoneNumber) {
            setPhoneNumber(selectedStaffProfile.organization.phoneNumber)
        }
        if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.location) {
            setLocation(selectedStaffProfile.organization.location)
        }
        if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.website) {
            setWebsite(selectedStaffProfile.organization.website)
        }
        if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.description) {
            setDescription(selectedStaffProfile.organization.description)
        }
        if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.headerImageURI) {
            dispatch(getOrganizationHeaderImage())
        }
    }, [authAccount])

    const onDropHeaderImage = useCallback(async acceptedFiles => {
        // Do something with the files
        console.log(acceptedFiles[0])
        try {
            if (selectedStaffProfile && selectedStaffProfile.organizationID) {
                await Storage.put(`organizations/${selectedStaffProfile.organizationID}/headerImage`, acceptedFiles[0], {
                    contentType: acceptedFiles[0].type
                })
                await Auth.currentCredentials()
                    .then(credentials => {
                        const s3 = new AWS.S3({ credentials: Auth.essentialCredentials(credentials), apiVersion: '2006-03-01' })
                        const upload = s3.upload({ Bucket: 'public-flourish-assets', Key: `organizations/${selectedStaffProfile.organizationID}/headerImage`, Body: acceptedFiles[0] })
                        upload.send()
                    })
                dispatch(getOrganizationHeaderImage())
                setChangeHeaderImage(false)
                dispatch(updateHeaderImage(`organizations/${selectedStaffProfile.organizationID}/headerImage`))
            }
        } catch (err) {
            console.log(err)
        }
    }, [])

    const onDropProfileImage = useCallback(async acceptedFiles => {
        // Do something with the files
        console.log(acceptedFiles[0])
        try {
            if (selectedStaffProfile && selectedStaffProfile.organizationID) {
                await Storage.put(`organizations/${selectedStaffProfile.organizationID}/profilePicture`, acceptedFiles[0], {
                    contentType: acceptedFiles[0].type
                })
                await Auth.currentCredentials()
                    .then(credentials => {
                        const s3 = new AWS.S3({ credentials: Auth.essentialCredentials(credentials), apiVersion: '2006-03-01' })
                        const upload = s3.upload({ Bucket: 'public-flourish-assets', Key: `organizations/${selectedStaffProfile.organizationID}/profilePicture`, Body: acceptedFiles[0] })
                        upload.send()
                    })
                setChangeProfilePicture(false)
            }
        } catch (err) {
            console.log(err)
        }
    }, [])

    const headerImageDropzone = useDropzone({ maxFiles: 1, accept: 'image/jpeg,image/png', onDrop: onDropHeaderImage, maxSize: 10485760 })
    const headerImageAcceptedFiles = headerImageDropzone.acceptedFiles
    const headerImageRootProps = headerImageDropzone.getRootProps
    const headerImageInputProps = headerImageDropzone.getInputProps
    const headerImageIsDragActive = headerImageDropzone.isDragActive

    const profilePictureDropzone = useDropzone({ maxFiles: 1, accept: 'image/jpeg,image/png', onDrop: onDropProfileImage, maxSize: 10485760 })
    const profilePictureAcceptedFiles = profilePictureDropzone.acceptedFiles
    const profilePictureRootProps = profilePictureDropzone.getRootProps
    const profilePictureInputProps = profilePictureDropzone.getInputProps
    const profilePictureIsDragActive = profilePictureDropzone.isDragActive

    return (
        <div>
            <Dialog
                open={visible}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{ zIndex: 99 }}
            >
                <DialogTitle id="alert-dialog-title">
                    Manage your organization details
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" className="mb-3">
                        Edit details about your organization here to help volunteers find out who you are.
                    </DialogContentText>
                    <div className="d-flex flex-row justify-content-between align-items-center my-3">
                        {
                            !changeProfilePicture ?
                                <ProfileAvatar organizationID={selectedStaffProfile && selectedStaffProfile.organization ? selectedStaffProfile.organization.id : null} className="my-3" size={100} />
                                :
                                <section className="my-3 d-flex flex-column justify-content-center" style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: 'black', height: 100, width: 100, borderRadius: 50 }}>
                                    <div {...profilePictureRootProps({ className: 'dropzone h-100 w-100' })} style={{ backgroundImage: uploadedProfilePictureFile ? uploadedProfilePictureFile.preview : null }}>
                                        <input {...profilePictureInputProps()} />
                                        {
                                            profilePictureIsDragActive ?
                                                <p style={{ width: '100%', textAlign: 'center' }}>Drop the files here</p>
                                                :
                                                <p style={{ width: '100%', textAlign: 'center' }}>Drag an image here, or click to select a file.</p>
                                        }
                                    </div>
                                </section>
                        }
                        {
                            !changeProfilePicture ?
                                <Button onClick={() => { setChangeProfilePicture(true) }}>
                                    Change Image
                                </Button>
                                :
                                <Button onClick={() => { setChangeProfilePicture(false) }}>
                                    Cancel
                                </Button>
                        }
                    </div>
                    <TextField
                        id="name"
                        variant="outlined"
                        type="text"
                        placeholder="Organization name"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        disabled
                        value={name}
                        className="my-3"
                        label="Organization name"
                    />
                    <TextField
                        id="shortName"
                        variant="outlined"
                        type="text"
                        placeholder="Your organization's acronym or abbreviation (optional)"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setShortName(e.target.value)
                        }}
                        value={shortName}
                        className="my-3"
                        label="Nickname / abbreviation"
                    />
                    <PhoneNumberInput
                        defaultCountry={'us'}
                        onChange={(e) => { if (typeof e == 'string') setPhoneNumber(e) }}
                        value={phoneNumber}
                        variant="outlined"
                        fullWidth
                        className="my-3"
                        label="Phone number"
                    />
                    <LocationSelector
                        value={location}
                        onChange={(location: Location | null) => setLocation(location)}
                        className="my-3"
                    />
                    <TextField
                        id="website"
                        variant="outlined"
                        type="text"
                        placeholder="Your organization's website or social media page"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setWebsite(e.target.value)
                        }}
                        value={website}
                        className="my-3"
                        label="Website"
                    />
                    <TextField
                        id="description"
                        variant="outlined"
                        type="text"
                        multiline
                        minRows={3}
                        placeholder="A little bit about your organization to help volunteers get to know you better"
                        inputProps={{ style: { fontSize: 16 } }}
                        fullWidth
                        onChange={(e) => {
                            setDescription(e.target.value)
                        }}
                        value={description}
                        className="my-3"
                        label="Description"
                    />
                    <Typography variant="body1" className="mb-3" sx={{ fontFamily: 'Gilroy-Bold' }}>Header Image</Typography>
                    {
                        (organizationHeaderImage || uploadedHeaderImageFile) && !changeHeaderImage ?
                            <section className="m-0 mb-5 d-flex flex-column justify-content-center" style={{ height: 300, width: '100%', borderRadius: BORDER_RADIUS }}>
                                <img src={uploadedHeaderImageFile ? uploadedHeaderImageFile : organizationHeaderImage} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: BORDER_RADIUS }} />
                                <Button style={{ alignSelf: 'flex-end' }} onClick={() => {
                                    if (!changeHeaderImage) {
                                        setChangeHeaderImage(true)
                                    }
                                }}>
                                    Change Image
                                </Button>
                            </section>
                            :
                            <section className="p-5 m-0 mb-3 d-flex flex-column justify-content-center" style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: 'black', height: 300, borderRadius: BORDER_RADIUS }}>
                                <div {...headerImageRootProps({ className: 'dropzone h-100 w-100' })} style={{ backgroundImage: uploadedHeaderImageFile ? uploadedHeaderImageFile.preview : null }}>
                                    <input {...headerImageInputProps()} />
                                    {
                                        headerImageIsDragActive ?
                                            <p style={{ width: '100%', textAlign: 'center' }}>Drop the files here</p>
                                            :
                                            <p style={{ width: '100%', textAlign: 'center' }}>Drag an image here, or click to select a file.</p>
                                    }
                                </div>
                                {
                                    changeHeaderImage ?
                                        <Button style={{ alignSelf: 'flex-end' }} onClick={() => {
                                            setChangeHeaderImage(false)
                                        }}>
                                            Cancel
                                        </Button>
                                        :
                                        <div></div>
                                }
                            </section>
                    }
                </DialogContent>
                <DialogActions className="mt-3">
                    {
                        !loading ?
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                <Button onClick={handleClose}>Cancel</Button>
                                <Button
                                    onClick={_handleSaveChanges}
                                    variant="contained"
                                >
                                    Save
                                </Button>
                            </div>
                            :
                            <CircularProgress />
                    }
                </DialogActions>
            </Dialog>
            <div id="map"></div>
        </div>
    )
}